import React, { PropsWithChildren, RefObject, useMemo, useRef, useState } from 'react';
import { Blocker, useBlocker } from 'react-router';
import { UnsavedChangesOverlay } from '../../component/UnsavedChangesOverlay/UnsavedChangesOverlay';
import {
    UnsavedChangesContext,
    UnsavedChangesGuardOptions,
    WithUnsavedChangesGuardOptions,
} from './UnsavedChangesContext';

interface UnsavedChangesBlockerEntry {
    id: string;
    blocker: UnsavedChangesGuardOptions;
}

export function UnsavedChangesContextProvider(props: PropsWithChildren): React.JSX.Element {
    const entriesRef = useRef<UnsavedChangesBlockerEntry[]>([]);

    // This blocker is not navigation-triggered but instead programatically triggered
    // This should be used when a user attempts to close an in-progress form without saving for instance
    const [blocker, setBlocker] = useState<InternalBlocker | undefined>(undefined); // onSave, onDiscard

    const contextValue = useMemo(() => {
        const register = (id: string, blocker: UnsavedChangesGuardOptions) => {
            const existing = entriesRef.current.find(x => x.id === id);
            if (existing != null) {
                existing.blocker = blocker;
            } else {
                entriesRef.current.push({ id, blocker });
            }
        };

        const unregister = (id: string) => {
            entriesRef.current = entriesRef.current.filter(x => x.id !== id);
        };

        const withUnsavedChangesGuard = (options: WithUnsavedChangesGuardOptions) => {
            const { onProceed, onResolve } = options;
            const blocked = entriesRef.current.some(x => x.blocker.hasUnsavedChanges());
            if (blocked) {
                setBlocker({
                    state: 'blocked',
                    onResolve,
                    reset: () => {
                        setBlocker(undefined);
                    },
                    proceed: () => {
                        setBlocker(undefined);
                        onProceed();
                    },
                });
            } else {
                onProceed();
            }
        };

        return {
            register,
            unregister,
            withUnsavedChangesGuard,
        };
    }, []);

    return (
        <UnsavedChangesContext.Provider value={contextValue}>
            {props.children}
            <UnsavedChangesOverlayInstance entriesRef={entriesRef} blocker={blocker} />
        </UnsavedChangesContext.Provider>
    );
}

type InternalBlocker = Omit<Blocker, 'location'> & Pick<WithUnsavedChangesGuardOptions, 'onResolve'>;

interface UnsavedChangesOverlayInstanceProps {
    entriesRef: RefObject<UnsavedChangesBlockerEntry[]>;
    blocker?: InternalBlocker;
}

// This component is broken out so that the use of useBlocker is isolated to the UnsavedChangesOverlay component
// and changes to blocker state do not trigger React reconciliation for all descendants of the UnsavedChangesContextProvider
function UnsavedChangesOverlayInstance(props: UnsavedChangesOverlayInstanceProps): React.JSX.Element {
    const { entriesRef, blocker: blockerProp } = props;

    const navigationBlocker = useBlocker(({ currentLocation, nextLocation }) => {
        const shouldBlock =
            currentLocation.pathname !== nextLocation.pathname &&
            entriesRef.current!.some(x => x.blocker.hasUnsavedChanges());
        return shouldBlock;
    });

    const blocker: InternalBlocker = blockerProp ?? navigationBlocker;

    const overlayProps = useMemo(() => {
        const open = blocker.state === 'blocked';

        const onKeep = () => {
            blocker.onResolve?.('keep');
            const entries = entriesRef.current!;
            for (const entry of entries) {
                entry.blocker.onKeep?.();
            }
            blocker.reset?.();
        };

        const onDiscard = () => {
            blocker.onResolve?.('discard');
            const entries = entriesRef.current!;
            for (const entry of entries) {
                entry.blocker.onDiscard?.();
            }
            blocker.proceed?.();
        };

        const onSave = async () => {
            blocker.onResolve?.('save');
            const entries = entriesRef.current!;
            let stopProceeding = false;

            // Serial because there is a race condition when waiting for change events after mutations.
            // Can be made parallel once we stop waiting on change events to come through at the realtime service framework level
            for (const entry of entries) {
                if (entry.blocker.hasUnsavedChanges()) {
                    try {
                        const validationErrors = await entry.blocker.onSave?.();
                        if (validationErrors != null) {
                            stopProceeding = true;
                        }
                    } catch (_ignored) {
                        stopProceeding = true;
                    }
                }
            }

            if (stopProceeding) {
                blocker.reset?.();
            } else {
                blocker.proceed?.();
            }
        };

        // This is not exactly foolproof in the case where there are multiple active unsaved change entries
        const container = entriesRef.current
            ?.find(x => x.blocker.overlayContainer?.() != null)
            ?.blocker.overlayContainer?.();

        return {
            open,
            onKeep,
            onDiscard,
            onSave,
            container,
        };
    }, [blocker.state]);

    return <UnsavedChangesOverlay {...overlayProps} />;
}
